<template>
  <div class="w-full p-4 bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900">
    <div class="mb-4">
      <div class="flex justify-between mb-2">
        <span class="text-sm font-medium text-gray-700 dark:text-gray-300">生成进度</span>
        <span class="text-sm font-medium text-gray-700 dark:text-gray-300">{{ progress.toFixed(1) }}%</span>
      </div>
      <div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
        <div
          class="bg-indigo-600 dark:bg-indigo-500 h-2 rounded-full transition-all duration-300"
          :style="{ width: `${progress}%` }"
        ></div>
      </div>
    </div>

    <div class="space-y-2">
      <div class="flex justify-between text-sm">
        <span class="text-gray-600 dark:text-gray-400">已处理话题</span>
        <span class="font-medium dark:text-gray-200">{{ processedTopics }} / {{ totalTopics }}</span>
      </div>
      <div class="flex justify-between text-sm">
        <span class="text-gray-600 dark:text-gray-400">已处理问题</span>
        <span class="font-medium dark:text-gray-200">{{ processedQuestions }} / {{ totalQuestions }}</span>
      </div>
      <div class="flex justify-between text-sm" v-if="failedQuestions > 0">
        <span class="text-red-600 dark:text-red-400">失败问题数</span>
        <span class="font-medium text-red-600 dark:text-red-400">{{ failedQuestions }}</span>
      </div>
      <div class="text-sm text-gray-600 dark:text-gray-400">当前处理: {{ currentTopic || "准备中..." }}</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from "vue";
import { Manager, Socket } from "socket.io-client";
import { useToken } from "@/hooks/useToken";
import toast from "@/utils/toaster";

const { getToken } = useToken();
const socket = ref<typeof Socket | null>(null);
const progress = ref(0);
const totalTopics = ref(0);
const processedTopics = ref(0);
const totalQuestions = ref(0);
const processedQuestions = ref(0);
const failedQuestions = ref(0);
const currentTopic = ref("");

const emit = defineEmits(["complete", "error"]);

onMounted(() => {
  const token = getToken();
  const manager = new Manager(import.meta.env.VITE_SOCKET_URL, {
    query: { token },
    transports: ["websocket"],
  });
  socket.value = manager.socket("/");

  if (!socket.value) return;

  socket.value.on("model_answer_progress", (data: any) => {
    progress.value = data.progress || 0;
    totalTopics.value = data.total_topics;
    processedTopics.value = data.processed_topics;
    totalQuestions.value = data.total_questions;
    processedQuestions.value = data.processed_questions;
    failedQuestions.value = data.failed_questions?.length || 0;
    currentTopic.value = data.current_topic;

    if (data.status === "error") {
      toast.error(data.error || "生成过程中出现错误");
      emit("error", data.error);
    }
  });

  socket.value.on("generation_complete", (data: any) => {
    toast.success("模型答案生成完成");
    emit("complete", data.result);
  });

  socket.value.on("error", (error: any) => {
    console.error("Socket error:", error);
    toast.error(error.message || "连接错误");
    emit("error", error);
  });

  socket.value.emit("start_model_answer_generation");
});

onUnmounted(() => {
  if (socket.value) {
    socket.value.disconnect();
  }
});
</script>
